import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown } from 'antd';

import RecordBadge from '../common/RecordBadge';
import { cardOwnerOf } from '../cards/CardVisuals';
import { buildActionMenu } from '../common/actionMenu';
import ItemAuditLine from '../items/ItemAuditLine';
import { KEBAB } from '../icons';
import { chartFormWord } from '../../utils/chartForms';
import { normalizeId } from '../../utils/ids';
import { chartFormGlyph } from './chartFormGlyphs';

/** Where a chart is read: its page, in the context collection. */
export const chartPath = (collectionId, chartId) => `/collections/${collectionId}/charts/${chartId}`;

/**
 * One saved chart on the Reports list (plan §6.1). Rows are TEXT, never live
 * thumbnails: running every chart to list it would be the fan-out the batch
 * run exists to prevent. A row reads, left to right and down:
 *
 *   the chart's name — the door to its page (a chart has no identity tile of
 *   its own: the badge is for collections, cards and items)
 *   its description, when it has one
 *   the card it counts, as the card's badge ("Every card" for a chart of
 *   every card), the form it is drawn as, and when it was made and changed
 *   (ItemAuditLine: who did each, in its pop-up)
 *
 * and its ⋯, in the action taxonomy (`actions`, from chartActions). The tier
 * is the section the row sits in.
 */
export default function ChartRow({ chart, collectionId, card, actions, onOpen }) {
	const id = normalizeId(chart?._id);
	const show = chart?.spec?.show || {};
	const Glyph = chartFormGlyph(show.type);
	const menu = buildActionMenu(actions || []);
	const everyCard = chart?.spec && chart.spec.card_id === null;
	const audit = {
		createdAt: chart?.createdAt,
		updatedAt: chart?.updatedAt,
		owner: chart?.createdBy,
		updatedBy: chart?.updatedBy,
	};
	return (
		<li className='reports-row' data-chart-id={id}>
			<div className='reports-row__main'>
				<Link className='reports-row__name' to={chartPath(collectionId, id)} onClick={onOpen}>{chart?.name || 'Untitled chart'}</Link>
				{chart?.description ? <p className='reports-row__description'>{chart.description}</p> : null}
				<div className='reports-row__meta'>
					{everyCard ? (
						<span className='reports-row__every-card'>Every card</span>
					) : card ? (
						// sm, as plan §6.1 says; a parent's, a sub-collection's or a lender's card is qualified (standards §1).
						<RecordBadge kind='card' record={card} title={card.name || 'Card'} size='sm' collection={cardOwnerOf(card)} className='reports-row__card' />
					) : null}
					<span className='reports-row__form'>
						<Glyph aria-hidden='true' className='reports-row__form-glyph' />
						{chartFormWord(show.type)}
					</span>
					<ItemAuditLine item={audit} className='reports-row__audit' />
				</div>
			</div>
			{menu ? (
				<Dropdown trigger={['click']} placement='bottomRight' menu={menu}>
					<Button className='reports-row__more' icon={<KEBAB />} aria-label={`More actions for ${chart?.name || 'this chart'}`} />
				</Dropdown>
			) : null}
		</li>
	);
}
